import { Component, h, Listen, Prop, State } from '@stencil/core';
import { store } from '../../../redux/store';
import { Cart, SplitOption } from '../../../redux/types';
import { Unsubscribe } from '@stencil/redux';
import { addError, addVoucher, initForOrder, initPayment, redeemGiftCard, removeErrorType, removeVoucher } from '../../../redux/actions';
import { VoucherStatus } from '../../../types/types';
import { ApiError } from '../../../api/client';
import { RootState } from '../../../redux/reducers';
import { formatPrice, t } from '../../../redux/utils';
import { ErrorType } from '../../rtr-errors/utils';

@Component({
  tag: 'rtr-cart-voucher',
  styleUrl: 'style.sass',
  shadow: true,
})
export class RentwareCartVoucher {
  @Prop({ mutable: true }) cartState: Cart;
  @Prop({ mutable: true }) orderReference: string;
  @Prop({ mutable: true }) splitOption: SplitOption;

  @Prop() onVoucherSuccess: () => void;

  @State() voucherStatus: VoucherStatus;
  @State() voucherCode: string = '';

  // addSuccess!: typeof addSuccess;
  addError!: typeof addError;
  removeErrorType!: typeof removeErrorType;
  addVoucher!: typeof addVoucher;
  initForOrder!: typeof initForOrder;
  removeVoucher!: typeof removeVoucher;
  redeemGiftCard!: typeof redeemGiftCard;
  initPayment!: typeof initPayment;
  unsubscribe!: Unsubscribe;

  componentWillLoad() {
    this.voucherStatus = VoucherStatus.Progress;
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      return {
        cartState: state.cart,
        orderReference: state.bookingDetails.orderDetails?.id,
        splitOption: state.app.paymentSplitOption,
      };
    });

    store.mapDispatchToProps(this, {
      initPayment,
      addVoucher,
      initForOrder,
      redeemGiftCard,
      removeVoucher,
      // addSuccess,
      addError,
      removeErrorType,
    });

    if (this.cartState && !!this.cartState.voucher) {
      this.voucherCode = this.cartState.voucher.code;
      this.voucherStatus = VoucherStatus.Done;
    }
  }

  disconnectedCallback() {
    this.unsubscribe();
  }

  @Listen('keydown')
  handleKeyDown(ev: KeyboardEvent) {
    if (ev.key === 'Enter') {
      if (this.voucherCode?.length !== 0) {
        this.handleSendVoucher();
      }
    }
  }

  handleDeleteVoucher = () => {
    this.removeVoucher(
      this.voucherCode,
      () => {
        this.initForOrder(() => {
          this.voucherStatus = VoucherStatus.Progress;
          this.voucherCode = '';

          // call init payment as in case of recurring it could affect payment plan
          this.initPayment({ orderReference: this.orderReference });
        });
      },
      undefined,
    );
  };

  handleSendVoucher = () => {
    this.addVoucher(
      this.voucherCode,
      () => {
        this.initForOrder(() => {
          this.removeErrorType(ErrorType.VoucherError);
          this.voucherStatus = VoucherStatus.Done;

          // call init payment as in case of recurring it could affect payment plan
          this.initPayment({ orderReference: this.orderReference });
        });

        this.onVoucherSuccess?.();
      },
      err => {
        if (err instanceof ApiError && err.data?.error && err.data.error === 'giftCard') {
          this.handleGiftCardCodeRedeem();
        } else {
          this.addError({ message: this.invalidGiftCardText, type: ErrorType.VoucherError });
        }
      },
    );
  };

  handleGiftCardCodeRedeem = () => {
    this.redeemGiftCard({
      orderReference: this.orderReference,
      code: this.voucherCode,
      splitOption: this.splitOption,
      onSuccess: () => {
        this.removeErrorType(ErrorType.VoucherError);
        // maybe to uncomment after feedback
        // this.addSuccess({ message: this.giftCardAsVoucher });
        this.voucherCode = '';
        this.initPayment({ orderReference: this.orderReference });
        this.initForOrder(null, this.orderReference);
      },
      onError: () => {
        this.addError({ message: this.invalidText, type: ErrorType.VoucherError });
      },
    });
  };

  handleChange = e => {
    this.voucherCode = e.target.value?.toUpperCase();
    if (e.target.value.length === 0) this.voucherStatus = VoucherStatus.Progress;
  };

  get addText() {
    return t('cart.addVoucher');
  }

  get invalidText() {
    return t('cart.invalidVoucher');
  }

  get invalidGiftCardText() {
    return t('payment.giftCardCodeInvalid');
  }

  get labelText() {
    return t('cart.voucherLabel');
  }

  get placeholderText() {
    return t('cart.voucherPlaceholder');
  }

  get voucherClass() {
    if (this.voucherStatus === VoucherStatus.Progress) {
      return 'VoucherContainer VoucherContainerWithInput';
    }

    return 'VoucherContainer';
  }

  render() {
    return (
      <div class={this.voucherClass}>
        {this.voucherStatus == VoucherStatus.Progress && (
          <div class="InputContainer">
            <div class="InputLabel">{this.labelText}</div>

            <input type="text" value={this.voucherCode} placeholder={this.placeholderText} onPaste={this.handleChange} onInput={this.handleChange} />

            {this.voucherCode.length > 0 && (
              <div class="ActionContainer GlobalButtonSVG Clickable" onClick={this.handleSendVoucher}>
                {t('checkout.redeem')}
              </div>
            )}
          </div>
        )}

        {this.voucherStatus == VoucherStatus.Done && (
          <div class="DoneContainer">
            <div class="DoneContainer">Voucher code</div>

            <div class="VoucherCodeContainer">
              {this.voucherCode}

              {(this.cartState.voucher.amount.unit === 'fixed-amount' && <span>-{formatPrice(this.cartState.voucher.amount.value * 100)}</span>) || (
                <span>-{this.cartState.voucher.amount.value}%</span>
              )}

              <div class="RemoveContainer">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" onClick={this.handleDeleteVoucher} class="GlobalButtonSVG">
                  <path d="M4.53551 4.53553L11.6066 11.6066M11.6066 4.53553L4.53551 11.6066" stroke-linecap="round" />
                </svg>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}
